import {
  EmployeeCohortMetricId,
  RegularMetricId,
} from '../../../../api/types-graphql';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { TimePeriodSegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-segmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { Languages } from '../../../../constants';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  DataTypes,
  EmployeeDataFields,
  TimeSelectionType,
} from '../../../../types';

// ============================================================================
// Time Selection Helpers
// ============================================================================

/**
 * Creates a time selection for a specific month (absolute date)
 * Mar'25 = 2025-03-31
 */
export const getMarch2025TimeSelection =
  (): CalendarYearSingleValueByMonthsTimeSelection => ({
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: '2025-03-31', end: '2025-03-31' },
  });

/**
 * Creates a time selection for December 2025
 * Dec'25 = 2025-12-31
 */
export const getDecember2025TimeSelection =
  (): CalendarYearSingleValueByMonthsTimeSelection => ({
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: '2025-12-31', end: '2025-12-31' },
  });

// ============================================================================
// Filter Constants
// ============================================================================

/** Filter: Custom Field4 = All except for "対象外" */
const customField4ExcludeFilter = `EMPLOYEE.CUSTOM_FIELD_4_LEVEL_1 != '対象外'`;

/** Filter: Organization Level1 = "ツーリズム" */
const tourismOrgLevel1Filter = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'ツーリズム'`;

/** Filter: Organization Level2 = "ツーリズム（法人）" */
const tourismCorporateOrgLevel2Filter = `EMPLOYEE.ORGANIZATION_LEVEL_2 = 'ツーリズム（法人）'`;

/** Filter: Organization Level2 = "ツーリズム（個人）" */
const tourismRetailOrgLevel2Filter = `EMPLOYEE.ORGANIZATION_LEVEL_2 = 'ツーリズム（個人）'`;

/** Filter: Organization Level3 = "西日本" */
const westJapanOrgLevel3Filter = `EMPLOYEE.ORGANIZATION_LEVEL_3 = '西日本'`;

// ============================================================================
// PAGE 1: Productivity Driver Analysis (労働時間生産性)
// ============================================================================

/**
 * Chart: Revenue per Workhour (労働時間あたり営業収入)
 * Location: Left Top
 * Type: Bar Chart grouped by Custom Field4
 * Note: MoM benchmark requested but not supported by TimePeriodUnsegmented component
 */
export const getRevenuePerWorkhourChart = (
  timeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => ({
  chartId: 'page1-revenue-per-workhour',
  layoutConfig: {
    gridColSpan: 10,
    gridRowSpan: 1,
  },
  componentConfig: {
    componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
    dataConfig: {
      title: {
        [Languages.EN]: 'Revenue per workhour',
        [Languages.JA]: '労働時間あたり営業収入',
      },
      queries: [
        {
          metric: {
            type: 'EmployeeCohortMetricId',
            value: EmployeeCohortMetricId.Pm0426_3RevenueMonthlyPerWorkHours,
          },
        },
      ],
      timeSelection,
      filters: customField4ExcludeFilter,
      groupBy: {
        type: 'non-hierarchical',
        dataField: {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.CUSTOM_FIELD_4_LEVEL_1,
        },
      },
    },
    selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
  },
});

/**
 * Chart: Individuals (従業員数)
 * Location: Left Bottom
 * Type: Bar Chart grouped by Custom Field4
 * Note: MoM benchmark requested but not supported by TimePeriodUnsegmented component
 */
export const getIndividualsChart = (
  timeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => ({
  chartId: 'page1-individuals',
  layoutConfig: {
    gridColSpan: 10,
    gridRowSpan: 1,
  },
  componentConfig: {
    componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
    dataConfig: {
      title: {
        [Languages.EN]: 'Individual',
        [Languages.JA]: '従業員数',
      },
      queries: [
        {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
        },
      ],
      timeSelection,
      filters: customField4ExcludeFilter,
      groupBy: {
        type: 'non-hierarchical',
        dataField: {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.CUSTOM_FIELD_4_LEVEL_1,
        },
      },
    },
    selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
  },
});

/**
 * Chart: Workhour (労働時間)
 * Location: Right Top
 * Type: Bar Chart grouped by Custom Field4
 * Note: MoM benchmark requested but not supported by TimePeriodUnsegmented component
 */
export const getWorkhourChart = (
  timeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => ({
  chartId: 'page1-workhour',
  layoutConfig: {
    gridColSpan: 10,
    gridRowSpan: 1,
  },
  componentConfig: {
    componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
    dataConfig: {
      title: {
        [Languages.EN]: 'Workhour',
        [Languages.JA]: '労働時間',
      },
      queries: [
        {
          metric: {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh,
          },
        },
      ],
      timeSelection,
      filters: customField4ExcludeFilter,
      groupBy: {
        type: 'non-hierarchical',
        dataField: {
          dataType: DataTypes.EMPLOYEE,
          dataField: EmployeeDataFields.CUSTOM_FIELD_4_LEVEL_1,
        },
      },
    },
    selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
  },
});

/**
 * Chart: Headcount by Employment Type (雇用区分別の従業員数（比率）)
 * Location: Right Bottom
 * Type: Stacked 100% Bar Chart
 * - X-axis (groupBy): Custom Field4
 * - Y-axis (segmentBy): Custom Field3, Level2
 * Note: User can toggle between grouped/stacked/percentage views using display controls
 */
export const getHeadcountByEmploymentTypeChart = (
  timeSelection: CalendarYearSingleValueByMonthsTimeSelection
): StoryboardItemConfig => ({
  chartId: 'page1-headcount-by-employment-type',
  layoutConfig: {
    gridColSpan: 10,
    gridRowSpan: 1,
  },
  componentConfig: {
    componentId: StoryboardComponentIds.TIME_PERIOD_SEGMENTED,
    dataConfig: {
      title: {
        [Languages.EN]: 'Headcount by Employment Type',
        [Languages.JA]: '雇用区分別の従業員数（比率）',
      },
      query: {
        metric: {
          type: 'RegularMetricId',
          value: RegularMetricId.Pm0001_1IndividualsCount,
        },
        timeSelection,
        filters: customField4ExcludeFilter,
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.CUSTOM_FIELD_4_LEVEL_1,
          },
        },
        segmentBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.CUSTOM_FIELD_3_LEVEL_2,
          },
        },
      },
    },
    selectedView: TimePeriodSegmentedVizViews.BAR_CHART,
  },
});

// ============================================================================
// PAGE 2 & 3: Overwork Alert (労働時間アラート)
// ============================================================================

/**
 * Combines multiple filters with AND
 */
const combineFilters = (...filters: string[]): string => filters.join(' AND ');

/** Organization type for filter selection */
export type OrgType = 'corporate' | 'retail';

/** Filter: Custom Field3 = "管理監督者" */
export const customField3SupervisorFilter = `EMPLOYEE.CUSTOM_FIELD_3_LEVEL_1 = '管理監督者'`;

/**
 * Gets the organization level 2 filter based on org type
 */
const getOrgLevel2Filter = (orgType: OrgType): string =>
  orgType === 'corporate'
    ? tourismCorporateOrgLevel2Filter
    : tourismRetailOrgLevel2Filter;

/**
 * Gets the title suffix based on org type
 */
const getOrgTypeTitle = (orgType: OrgType): { en: string; ja: string } =>
  orgType === 'corporate'
    ? { en: 'Corporate', ja: 'ツーリズム法人' }
    : { en: 'Retail', ja: 'ツーリズム個人' };

/**
 * Generic Factbox: Average Work Hours
 * @param timeSelection - Time selection for the query
 * @param orgType - Organization type (corporate or retail)
 * @param options - Optional configuration
 * @param options.additionalFilter - Additional filter to apply (e.g., managers filter)
 * @param options.chartIdSuffix - Suffix for chart ID to ensure uniqueness
 */
export const getAvgWorkhourFactbox = (
  timeSelection: CalendarYearSingleValueByMonthsTimeSelection,
  orgType: OrgType,
  options?: {
    additionalFilter?: string;
    chartIdSuffix?: string;
  }
): StoryboardItemConfig => {
  const orgTitle = getOrgTypeTitle(orgType);
  const baseFilters = [
    tourismOrgLevel1Filter,
    getOrgLevel2Filter(orgType),
    westJapanOrgLevel3Filter,
  ];

  if (options?.additionalFilter) {
    baseFilters.push(options.additionalFilter);
  }

  return {
    chartId: `avg-workhour-${orgType}${options?.chartIdSuffix ?? ''}`,
    layoutConfig: {
      gridColSpan: 10,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: `Average Work Hours (${orgTitle.en})`,
          [Languages.JA]: `平均労働時間（${orgTitle.ja}）`,
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh,
            },
            timeSelection,
            filters: combineFilters(...baseFilters),
          },
          benchmarkConfigs: [
            {
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

/**
 * Generic Chart: Average Work Hours by Branch
 * @param timeSelection - Time selection for the query
 * @param orgType - Organization type (corporate or retail)
 * @param options - Optional configuration
 * @param options.additionalFilter - Additional filter to apply (e.g., managers filter)
 * @param options.chartIdSuffix - Suffix for chart ID to ensure uniqueness
 */
export const getAvgWorkhourByBranchChart = (
  timeSelection: CalendarYearSingleValueByMonthsTimeSelection,
  orgType: OrgType,
  options?: {
    additionalFilter?: string;
    chartIdSuffix?: string;
  }
): StoryboardItemConfig => {
  const orgTitle = getOrgTypeTitle(orgType);
  const baseFilters = [
    tourismOrgLevel1Filter,
    getOrgLevel2Filter(orgType),
    westJapanOrgLevel3Filter,
  ];

  if (options?.additionalFilter) {
    baseFilters.push(options.additionalFilter);
  }

  return {
    chartId: `avg-workhour-by-branch-${orgType}${options?.chartIdSuffix ?? ''}`,
    layoutConfig: {
      gridColSpan: 10,
      gridRowSpan: 1,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: `By Branch (${orgTitle.en})`,
          [Languages.JA]: `拠点別（${orgTitle.ja}）`,
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0213_2AvgWorkHoursPerPersonAvgWithWh,
            },
          },
        ],
        timeSelection,
        filters: combineFilters(...baseFilters),
        groupBy: {
          type: 'non-hierarchical',
          dataField: {
            dataType: DataTypes.EMPLOYEE,
            dataField: EmployeeDataFields.ORGANIZATION_LEVEL_4,
          },
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};
